import {
    Box,
    Stack,
    ToggleButton,
    ToggleButtonGroup,
    Typography,
} from '@mui/material';
import { ChartSpline, Circle, Diameter, Spline } from 'lucide-react';
import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilState, useRecoilValue, useSetRecoilState } from 'recoil';
import DisabledHint from '~/components/Tooltips/DisabledHint';
import AnnotationSeriesGroupList from '~/components/toolbar/shared/AnnotationSeriesGroupList';
import AnnotationClearAllButton from '~/components/Annotations/AnnotationClearAllButton';
import { AnnotationRowLabelIds } from '~/components/Annotations/AnnotationRowActions';
import { euclideanDistanceMm } from '~/domain/geometry/patientGeometry';
import { CircleRegion } from '~/domain/intensity/circleRegion';
import { LineProfile } from '~/domain/intensity/lineProfile';
import { useIsDoseShown } from '~/hooks/dose/useDoseVisibility';
import { usePrimaryImageCompletenessGate } from '~/hooks/image/useImageStreamProgress';
import { useCircleRegionController } from '~/hooks/useCircleRegionController';
import { useLineProfileController } from '~/hooks/useLineProfileController';
import {
    circleRegionListState,
    IntensityShape,
    intensityShapeState,
    lineProfileListState,
    selectedIntensityAnnotationState,
    shownCircleRegionState,
    shownLineProfileState,
} from '~/state/atoms/intensityToolAtoms';
import { formatDistanceCm } from '~/utils/measurementFormat';
import IntensityAnnotationRow from './IntensityAnnotationRow';
import CircleRegionDetail from './CircleRegionDetail';
import LineProfileDetail from './LineProfileDetail';

const CIRCLE_LABEL_IDS: AnnotationRowLabelIds = {
    show: 'circleRegion.show',
    hide: 'circleRegion.hide',
    delete: 'circleRegion.delete',
};

const PROFILE_LABEL_IDS: AnnotationRowLabelIds = {
    show: 'lineProfile.show',
    hide: 'lineProfile.hide',
    delete: 'lineProfile.delete',
};

/** The row's main value: a circle's diameter, a line's length. */
const circleLabel = (circle: CircleRegion): string =>
    `⌀ ${formatDistanceCm(2 * circle.radiusMm)}`;
const profileLabel = (profile: LineProfile): string =>
    formatDistanceCm(euclideanDistanceMm(profile.startMm, profile.endMm));

/**
 * The tool's panel: a shape toggle for what the next click places, the list
 * of that shape, and the selected one's detail below — charts for a line,
 * statistics for a circle.
 *
 * The shape also picks which kind is listed; everything placed stays drawn
 * and editable either way. One selection across both kinds, defaulting to
 * the newest of the listed shape.
 *
 * The circle is refused while the image is not whole, as its read refuses it
 * (SR-WOR-H-37-3), and while a dose is shown, where its figures would be
 * read against the dose. Circles of more than one image are listed under a
 * header naming each image, as measurements are, so two figures read from
 * two scans cannot be taken for one another.
 *
 * @param allowCircle false on the dose step (SR-WOR-H-17-5). The shape is a
 *   single app-wide atom, so an unavailable one is reset rather than merely
 *   hidden: otherwise the engine would still build the circle tool from it.
 */
export default function IntensityPanel({
    allowCircle = true,
}: {
    allowCircle?: boolean;
}) {
    const intl = useIntl();
    const [shape, setShape] = useRecoilState(intensityShapeState);
    const setSelectedId = useSetRecoilState(selectedIntensityAnnotationState);
    const imageGate = usePrimaryImageCompletenessGate();
    const doseShown = useIsDoseShown();
    /** Why the circle is refused; undefined when it is offered. */
    const circleRefusal = !allowCircle
        ? intl.formatMessage({ id: 'intensity.shape.circle.notOnDose' })
        : doseShown
          ? intl.formatMessage({
                id: 'intensity.shape.circle.notWhileDoseShown',
            })
          : imageGate.reason;

    useEffect(() => {
        if (circleRefusal !== undefined && shape === 'circle') {
            setShape('line');
        }
    }, [circleRefusal, shape, setShape]);

    // Both shapes are listed here, so each set of list actions is named for
    // the shape it acts on.
    const {
        ids: profileIds,
        remove: removeProfile,
        toggleVisibility: toggleProfile,
        clearAll: clearProfiles,
        setHighlighted: highlightProfile,
        goTo: goToProfile,
        isObliqueToImage: isProfileOblique,
        comparisonForProfile,
    } = useLineProfileController();
    const {
        ids: circleIds,
        remove: removeCircle,
        toggleVisibility: toggleCircle,
        clearAll: clearCircles,
        setHighlighted: highlightCircle,
        goTo: goToCircle,
        isObliqueToImage: isCircleOblique,
        imageForCircle,
        groups: circleGroups,
        showSeriesLabels: showCircleSeriesLabels,
    } = useCircleRegionController();
    const shownProfile = useRecoilValue(shownLineProfileState);
    const shownCircle = useRecoilValue(shownCircleRegionState);

    // One kind at a time keeps every row the same height. Both stay drawn on
    // the image, so filtering the list hides nothing that was placed.
    const showingCircles = shape === 'circle';
    const listed = showingCircles ? circleIds.length : profileIds.length;

    const handleShapeChange = (
        _event: React.MouseEvent<HTMLElement>,
        next: IntensityShape | null
    ) => {
        if (next) {
            setShape(next);
        }
    };

    // One definition, offered or refused, so the two cannot drift apart.
    const circleButton = (
        <ToggleButton value="circle" disabled={circleRefusal !== undefined}>
            <Diameter size={15} style={{ marginRight: 6 }} />
            {intl.formatMessage({ id: 'intensity.shape.circle' })}
        </ToggleButton>
    );

    return (
        <Box>
            {/* Halves set here, not by `fullWidth`: that sizes ToggleButton
                children only, and the disabled circle sits inside
                DisabledHint's span. */}
            <ToggleButtonGroup
                value={shape}
                exclusive
                size="small"
                onChange={handleShapeChange}
                sx={{
                    mb: 1,
                    display: 'flex',
                    '& > *': { flex: '1 1 0' },
                    '& .MuiToggleButton-root': { py: 0.25, flex: '1 1 0' },
                }}
            >
                <ToggleButton value="line">
                    <ChartSpline size={15} style={{ marginRight: 6 }} />
                    {intl.formatMessage({ id: 'intensity.shape.line' })}
                </ToggleButton>
                {circleRefusal === undefined ? (
                    circleButton
                ) : (
                    <DisabledHint
                        title={circleRefusal}
                        spanStyle={{ display: 'flex' }}
                    >
                        {circleButton}
                    </DisabledHint>
                )}
            </ToggleButtonGroup>

            {listed === 0 && (
                <Typography
                    variant="caption"
                    sx={{ display: 'block', color: 'text.secondary', mb: 1 }}
                >
                    {intl.formatMessage({
                        id:
                            shape === 'circle'
                                ? 'circleRegion.hint'
                                : 'lineProfile.hint',
                    })}
                </Typography>
            )}

            {listed > 0 && (
                <>
                    <Stack
                        direction="row"
                        alignItems="center"
                        justifyContent="space-between"
                        sx={{ mb: 0.5 }}
                    >
                        <Typography
                            variant="caption"
                            sx={{ color: 'text.secondary', pl: 0.5 }}
                        >
                            {intl.formatMessage({
                                id: showingCircles
                                    ? 'circleRegion.circles'
                                    : 'lineProfile.profiles',
                            })}{' '}
                            ({listed})
                        </Typography>
                        {/* Clears the listed shape only: clearing what the
                            panel is not showing would be a surprise. */}
                        {/* Remounted per shape: an armed confirmation must
                            not carry across the toggle onto the other list. */}
                        <AnnotationClearAllButton
                            key={shape}
                            itemCount={listed}
                            onClear={
                                showingCircles ? clearCircles : clearProfiles
                            }
                            labelId={
                                showingCircles
                                    ? 'circleRegion.clearAll'
                                    : 'lineProfile.clearAll'
                            }
                            confirmLabelId={
                                showingCircles
                                    ? 'circleRegion.clearAll.confirm'
                                    : 'lineProfile.clearAll.confirm'
                            }
                        />
                    </Stack>
                    <Stack spacing={0.5}>
                        {showingCircles && (
                            <AnnotationSeriesGroupList
                                groups={circleGroups}
                                showSeriesLabels={showCircleSeriesLabels}
                                renderRow={(id) => (
                                    <IntensityAnnotationRow
                                        list={circleRegionListState}
                                        id={id}
                                        icon={<Circle size={17} />}
                                        label={circleLabel}
                                        isOblique={isCircleOblique}
                                        selected={id === shownCircle?.id}
                                        onActivate={() => {
                                            setSelectedId(id);
                                            goToCircle(id);
                                        }}
                                        onToggle={() => toggleCircle(id)}
                                        onDelete={() => removeCircle(id)}
                                        onHover={(hovered) =>
                                            highlightCircle(hovered ? id : null)
                                        }
                                        labelIds={CIRCLE_LABEL_IDS}
                                    />
                                )}
                            />
                        )}
                        {!showingCircles &&
                            profileIds.map((id) => (
                                <IntensityAnnotationRow
                                    key={id}
                                    list={lineProfileListState}
                                    id={id}
                                    icon={<Spline size={17} />}
                                    label={profileLabel}
                                    isOblique={isProfileOblique}
                                    selected={id === shownProfile?.id}
                                    onActivate={() => {
                                        setSelectedId(id);
                                        goToProfile(id);
                                    }}
                                    onToggle={() => toggleProfile(id)}
                                    onDelete={() => removeProfile(id)}
                                    onHover={(hovered) =>
                                        highlightProfile(hovered ? id : null)
                                    }
                                    labelIds={PROFILE_LABEL_IDS}
                                />
                            ))}
                    </Stack>
                    {/* Dimmed with its row when hidden: values for something
                        no longer drawn must not look live. */}
                    {showingCircles && shownCircle && (
                        <Box sx={{ opacity: shownCircle.visible ? 1 : 0.5 }}>
                            <CircleRegionDetail
                                circle={shownCircle}
                                image={imageForCircle(shownCircle)}
                            />
                        </Box>
                    )}
                    {!showingCircles && shownProfile && (
                        <Box sx={{ opacity: shownProfile.visible ? 1 : 0.5 }}>
                            <LineProfileDetail
                                profile={shownProfile}
                                comparisonForProfile={comparisonForProfile}
                            />
                        </Box>
                    )}
                </>
            )}
        </Box>
    );
}
